import React from 'react';
import styled from 'styled-components';

import { capitalize, Chip, Divider, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Label } from '@common/Label';
import { v4 as uuidv4 } from 'uuid';
import { Expression } from '@common/Expression';
import { HTTPRequestMetadata } from '../../../tool.types';
import { Option } from '@common/BucketInput/BucketField';
import { Select } from '@common/Select';
import { RequestParameterField } from './RequestParameterField';
import { Button } from '@common/Button';
import { AddOutlined } from '@mui/icons-material';
import { useTheme } from '../../../../../../../../../styles/theme';
import { Secrets } from '@common/Autocomplete/Secrets';
import { JSONEditor } from '@common/JSONEditor/JSONEditor';
import { useSecrets } from '@common/hooks/useSecrets';

interface Props {
  metadata: HTTPRequestMetadata;
  setToolMetadata: React.Dispatch<React.SetStateAction<HTTPRequestMetadata>>;
  toolArgumentOptions: object;
}

const Parameters = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  width: 100%;
  min-width: 0px;
`;

export const HTTPRequestExecution: React.FC<Props> = ({ metadata, setToolMetadata, toolArgumentOptions }) => {
  const theme = useTheme();

  const { secrets, loading, refetch } = useSecrets();

  const hasQueryParams = metadata.query_parameters.length > 0;
  const hasBodyParams = metadata.body_parameters.length > 0;
  const hasHeaderParams = metadata.header_parameters.length > 0;

  const secret = secrets.find((secret) => secret.identifier === metadata.auth_secret_name);
  const secretType = secret?.type;

  const updateTextField = (
    paramName:
      | 'url'
      | 'method'
      | 'auth_enabled'
      | 'auth_secret_name'
      | 'body_json'
      | 'body_format'
      | 'approval_required',
    value: string
  ) => {
    setToolMetadata((prevState) => ({ ...prevState, [paramName]: value }));
  };

  const addField = (paramName: 'query_parameters' | 'body_parameters' | 'header_parameters') => {
    const getType = (): 'query' | 'body' | 'header' => {
      if (paramName === 'query_parameters') return 'query';
      if (paramName === 'body_parameters') return 'body';
      if (paramName === 'header_parameters') return 'header';
      return 'query';
    };

    const type = getType();
    const newField = { id: uuidv4(), name: '', value: '', type, required: true };
    setToolMetadata((prevState) => ({ ...prevState, [paramName]: prevState[paramName].concat(newField) }));
  };

  const updateField = (
    id: string,
    paramName: 'query_parameters' | 'body_parameters' | 'header_parameters',
    field: 'name' | 'value',
    value: string
  ) => {
    setToolMetadata((prevState) => ({
      ...prevState,
      [paramName]: prevState[paramName].map((item) => (item.id === id ? { ...item, [field]: value } : item)),
    }));
  };

  const deleteField = (id: string, paramName: 'query_parameters' | 'body_parameters' | 'header_parameters') => {
    setToolMetadata((prevState) => ({
      ...prevState,
      [paramName]: prevState[paramName].filter((param) => param.id !== id),
    }));
  };

  const searchTypeOptions: Option[] = [
    { label: 'GET', value: 'get' },
    { label: 'POST', value: 'post' },
  ];

  return (
    <Stack spacing={2}>
      <Stack spacing={1}>
        <Label title='Method' tooltip='The HTTP method to use for this request.' />
        <Select
          value={metadata.method}
          setValue={(value: string) => updateTextField('method', value)}
          options={searchTypeOptions}
          placeholder='HTTP Method'
        />
      </Stack>
      <Stack spacing={1}>
        <Label title='URL' tooltip='The URL to send the request to.' />
        <Expression
          key={`url`}
          keyProp={`url`}
          value={metadata.url}
          title={'URL'}
          description={'The URL to send the request to.'}
          setValue={(value: string) => updateTextField('url', value)}
          options={toolArgumentOptions}
          placeholder='https://example.com'
        />
      </Stack>
      <Stack spacing={1}>
        <Label
          title='Require Approval'
          tooltip='If enabled, the tool execution parameters will be formatted by the LLM, and the user will have to run the tool by manually clicking "Approve".'
        />
        <ToggleButtonGroup
          size='small'
          color='primary'
          value={metadata.approval_required || 'false'}
          exclusive
          onChange={(_, newValue: string) => updateTextField('approval_required', newValue)}
          fullWidth
        >
          <ToggleButton size='small' value='true'>
            Enabled
          </ToggleButton>
          <ToggleButton size='small' value='false'>
            Disabled
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <Stack spacing={1}>
        <Label title='Authentication' tooltip='Send authentication headers with the request using a secret value.' />
        <ToggleButtonGroup
          size='small'
          color='primary'
          value={metadata.auth_enabled}
          exclusive
          onChange={(_, newValue: string) => updateTextField('auth_enabled', newValue)}
          fullWidth
        >
          <ToggleButton size='small' value='true'>
            Auth
          </ToggleButton>
          <ToggleButton size='small' value='false'>
            No Auth
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      {metadata.auth_enabled === 'true' && (
        <Stack spacing={1}>
          <Stack direction='row' justifyContent={'space-between'} alignItems='center'>
            <Label
              title='Secret'
              tooltip='The secret value to be used as the authorization token. The type of authentication used is dependent on the type of secret selected (bearer or basic).'
            />
            {secretType && (
              <Chip
                color={secretType === 'bearer' ? 'info' : 'primary'}
                label={capitalize(secretType)}
                size='small'
                variant='outlined'
                sx={{ fontSize: '1.3ch', height: '19px !important' }}
              />
            )}
          </Stack>
          <Secrets
            refetch={refetch}
            value={metadata.auth_secret_name}
            loading={loading}
            secrets={secrets}
            onChange={(_, item) => {
              if (!item?.value) return;
              updateTextField('auth_secret_name', item.value.identifier);
            }}
            placeholder='Secret'
          />
        </Stack>
      )}
      <Divider />
      <Stack spacing={2}>
        <Stack spacing={1}>
          <Label
            title='Headers'
            tooltip='Add headers to the request.'
            onClick={hasHeaderParams ? () => addField('header_parameters') : undefined}
          />
          <Parameters>
            {metadata.header_parameters.map((param) => (
              <RequestParameterField
                key={param.id}
                parameter={param}
                toolArgumentOptions={toolArgumentOptions}
                updateField={(field, value) => updateField(param.id, 'header_parameters', field, value)}
                deleteField={() => deleteField(param.id, 'header_parameters')}
              />
            ))}
            {!hasHeaderParams && (
              <Button color='primary' fullWidth onClick={() => addField('header_parameters')}>
                <AddOutlined sx={{ fontSize: '14px', color: theme.text.standard }} />
              </Button>
            )}
          </Parameters>
        </Stack>
        <Stack spacing={1}>
          <Label
            title='Query Parameters'
            tooltip='Add query parameters to the request.'
            onClick={hasQueryParams ? () => addField('query_parameters') : undefined}
          />
          <Parameters>
            {metadata.query_parameters.map((param) => (
              <RequestParameterField
                key={param.id}
                parameter={param}
                toolArgumentOptions={toolArgumentOptions}
                updateField={(field, value) => updateField(param.id, 'query_parameters', field, value)}
                deleteField={() => deleteField(param.id, 'query_parameters')}
              />
            ))}
            {!hasQueryParams && (
              <Button color='primary' fullWidth onClick={() => addField('query_parameters')}>
                <AddOutlined sx={{ fontSize: '14px', color: theme.text.standard }} />
              </Button>
            )}
          </Parameters>
        </Stack>
        {metadata.method === 'post' && (
          <Stack spacing={1}>
            <Label
              title='Body Format'
              tooltip='Specify how to format the body of the request. The "FORM" input type is used for simple values, and the "JSON" input type is used for complex data structures like a nested object.'
            />
            <ToggleButtonGroup
              size='small'
              color='primary'
              value={metadata.body_format}
              exclusive
              onChange={(_, newValue: string) => updateTextField('body_format', newValue)}
              fullWidth
            >
              <ToggleButton size='small' value='form'>
                Form
              </ToggleButton>
              <ToggleButton size='small' value='json'>
                JSON
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        )}
        {metadata.method === 'post' && metadata.body_format === 'form' && (
          <Stack spacing={1}>
            <Label
              title='Body Parameters'
              tooltip='Add body parameters to the request.'
              onClick={hasBodyParams ? () => addField('body_parameters') : undefined}
            />
            <Parameters>
              {metadata.body_parameters.map((param) => (
                <RequestParameterField
                  key={param.id}
                  parameter={param}
                  toolArgumentOptions={toolArgumentOptions}
                  updateField={(field, value) => updateField(param.id, 'body_parameters', field, value)}
                  deleteField={() => deleteField(param.id, 'body_parameters')}
                />
              ))}
              {!hasBodyParams && (
                <Button color='primary' fullWidth onClick={() => addField('body_parameters')}>
                  <AddOutlined sx={{ fontSize: '14px', color: theme.text.standard }} />
                </Button>
              )}
            </Parameters>
          </Stack>
        )}
      </Stack>
      {metadata.method === 'post' && metadata.body_format === 'json' && (
        <Stack spacing={1}>
          <Label
            title='Body Parameters'
            tooltip='Define the output schema for your request body. The output must be a valid JSON object.'
          />
          <JSONEditor
            value={metadata.body_json}
            setValue={(newValue: string) => updateTextField('body_json', newValue)}
            options={toolArgumentOptions}
          />
        </Stack>
      )}
    </Stack>
  );
};
